import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, Star, ArrowUp, ArrowDown, Filter, Upload, X, ImageIcon } from 'lucide-react';
import { getPortfolio, createPortfolio, updatePortfolio, updatePortfolioFields, updatePortfolioStatus, deletePortfolio, imageUrl } from '../services/api';
import { Table } from '../components/Tables';
import { Modal, Button } from '../components/Forms';
import { useForm } from 'react-hook-form';

const categories = [
  'Logo', 'Visiting Card', 'Social Media', 'Poster', 'Pamphlet',
  'Website', 'Banners', 'Resume', 'Invitation E-Card', 'Strategy',
  'Branding', 'SEO', 'Email', 'Creative', 'Social',
];

const PortfolioManagement = () => {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm();

  // image upload state
  const [featuredFile, setFeaturedFile] = useState(null);
  const [featuredPreview, setFeaturedPreview] = useState('');
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);
  const featuredInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPortfolio();
      setAllItems(data);
    } catch (err) {
      console.error('[Portfolio] Failed to load:', err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  let items = allItems;
  if (search) items = items.filter((i) => i.title?.toLowerCase().includes(search.toLowerCase()) || i.client?.toLowerCase().includes(search.toLowerCase()));
  if (categoryFilter) items = items.filter((i) => i.category === categoryFilter);

  const resetImageState = () => {
    setFeaturedFile(null);
    setFeaturedPreview('');
    setGalleryFiles([]);
    setExistingGallery([]);
    if (featuredInputRef.current) featuredInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const openAdd = () => {
    setEditing(null);
    reset({ title: '', description: '', category: '', client: '', status: 'active', featured: false });
    resetImageState();
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setValue('title', item.title);
    setValue('description', item.description);
    setValue('category', item.category);
    setValue('client', item.client || '');
    setValue('status', item.status);
    setValue('featured', item.featured || false);
    resetImageState();
    setFeaturedPreview(item.featuredImage ? imageUrl(item.featuredImage) : '');
    setExistingGallery(item.galleryImages || []);
    setModalOpen(true);
  };

  const handleFeaturedChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFeaturedFile(file);
    setFeaturedPreview(URL.createObjectURL(file));
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setGalleryFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeNewGalleryImage = (idx) => setGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
  const removeExistingGalleryImage = (idx) => setExistingGallery((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => formData.append(k, v));
    if (featuredFile) formData.append('featuredImage', featuredFile);
    galleryFiles.forEach((f) => formData.append('galleryImages', f));
    try {
      if (editing) {
        formData.append('existingGalleryImages', JSON.stringify(existingGallery));
        await updatePortfolio(editing._id, formData);
      } else {
        await createPortfolio(formData);
      }
      setModalOpen(false);
      resetImageState();
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save portfolio item');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await deletePortfolio(item._id);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  const toggleStatus = async (item) => {
    try {
      await updatePortfolioStatus(item._id, item.status === 'active' ? 'inactive' : 'active');
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await updatePortfolioFields(item._id, { featured: !item.featured });
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update');
    }
  };

  const moveItem = async (item, direction) => {
    const sorted = [...allItems].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const idx = sorted.findIndex((i) => i._id === item._id);
    if (idx < 0) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const other = sorted[targetIdx];
    try {
      await Promise.all([
        updatePortfolioFields(item._id, { order: targetIdx }),
        updatePortfolioFields(other._id, { order: idx }),
      ]);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reorder');
    }
  };

  const uniqueCategories = [...new Set(allItems.map((i) => i.category).filter(Boolean))];

  const columns = [
    {
      key: 'order', label: 'Order',
      render: (v, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => moveItem(row, -1)} className="p-0.5 text-slate-400 hover:text-purple-600" title="Move up"><ArrowUp size={14} /></button>
          <button onClick={() => moveItem(row, 1)} className="p-0.5 text-slate-400 hover:text-purple-600" title="Move down"><ArrowDown size={14} /></button>
        </div>
      ),
    },
    {
      key: 'featuredImage', label: 'Image',
      render: (v, row) => v ? (
        <img src={imageUrl(v)} alt={row.title} className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300"><ImageIcon size={18} /></div>
      ),
    },
    { key: 'title', label: 'Title', sortable: true },
    {
      key: 'category', label: 'Category', sortable: true,
      render: (v) => <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{v}</span>,
    },
    { key: 'client', label: 'Client', sortable: true },
    {
      key: 'galleryImages', label: 'Photos',
      render: (v) => <span className="text-xs text-slate-500">{v?.length || 0} photo{(v?.length || 0) === 1 ? '' : 's'}</span>,
    },
    {
      key: 'featured', label: 'Featured',
      render: (v, row) => (
        <button onClick={() => toggleFeatured(row)} className={`p-1 rounded transition-colors ${v ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}>
          <Star size={16} fill={v ? 'currentColor' : 'none'} />
        </button>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (v, row) => (
        <button onClick={() => toggleStatus(row)} className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer border-none ${v === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
          {v}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title or client..." className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm" />
          </div>
          <div className="relative w-full sm:w-44">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm bg-white appearance-none">
              <option value="">All Categories</option>
              {uniqueCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={openAdd}><Plus size={18} /> Add Portfolio Item</Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <Table columns={columns} data={items} onEdit={openEdit} onDelete={handleDelete} loading={loading} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Portfolio Item' : 'Add Portfolio Item'} size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
              <input {...register('title', { required: 'Title is required' })} className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm" />
              {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Category *</label>
              <select {...register('category', { required: 'Category is required' })} className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm bg-white">
                <option value="">Select category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category.message}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description *</label>
            <textarea {...register('description', { required: 'Description is required' })} rows={3} className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm resize-none" />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>}
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Featured Image</label>
            <div className="flex items-start gap-4">
              {featuredPreview ? (
                <div className="relative group">
                  <img src={featuredPreview} alt="Featured preview" className="w-28 h-28 rounded-lg object-cover border border-slate-200" />
                  <button type="button" onClick={() => { setFeaturedFile(null); setFeaturedPreview(''); if (featuredInputRef.current) featuredInputRef.current.value = ''; }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600" title="Remove">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => featuredInputRef.current?.click()} className="w-28 h-28 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-purple-400 hover:text-purple-500 transition-colors">
                  <Upload size={20} />
                  <span className="text-xs mt-1">Upload</span>
                </button>
              )}
              <div className="text-xs text-slate-500 pt-1">
                <p>Main image shown on the portfolio card.</p>
                <p className="mt-1">JPG, PNG, WEBP, GIF — max 10MB.</p>
                {featuredPreview && (
                  <button type="button" onClick={() => featuredInputRef.current?.click()} className="mt-2 text-purple-600 hover:text-purple-700 font-medium">Change image</button>
                )}
              </div>
            </div>
            <input ref={featuredInputRef} type="file" accept="image/*" onChange={handleFeaturedChange} className="hidden" />
          </div>

          {/* Gallery Images */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Gallery Photos <span className="font-normal text-slate-400">(multiple allowed)</span></label>
            <div className="flex flex-wrap gap-3">
              {existingGallery.map((img, idx) => (
                <div key={`existing-${idx}`} className="relative group">
                  <img src={imageUrl(img)} alt={`Gallery ${idx + 1}`} className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
                  <button type="button" onClick={() => removeExistingGalleryImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600" title="Remove">
                    <X size={12} />
                  </button>
                </div>
              ))}
              {galleryFiles.map((file, idx) => (
                <div key={`new-${idx}`} className="relative group">
                  <img src={URL.createObjectURL(file)} alt={`New ${idx + 1}`} className="w-20 h-20 rounded-lg object-cover border-2 border-purple-300" />
                  <button type="button" onClick={() => removeNewGalleryImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600" title="Remove">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => galleryInputRef.current?.click()} className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-purple-400 hover:text-purple-500 transition-colors">
                <Plus size={18} />
                <span className="text-[10px] mt-0.5">Add photos</span>
              </button>
            </div>
            <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={handleGalleryChange} className="hidden" />
            <p className="text-xs text-slate-400 mt-1.5">Select multiple photos at once — up to 20 per upload.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Client Name</label>
            <input {...register('client')} className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm" />
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700">Status</label>
              <select {...register('status')} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm bg-white">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" {...register('featured')} className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
              <span className="text-sm font-medium text-slate-700">Featured Project</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button variant="secondary" onClick={() => setModalOpen(false)} type="button">Cancel</Button>
            <Button loading={isSubmitting} type="submit">{editing ? 'Update' : 'Add'} Item</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PortfolioManagement;
